import { useQuery } from '@tanstack/react-query'
import { graphqlQuery } from '@/services/graphql'
import { useAuthStore } from '@/store/authStore'
import { setSentryUser } from '@/config/sentry'
import { identifyMixpanelUser } from '@/config/mixpanel'
import { setUserContext } from '@/lib/analytics'
import type { User, UserProfile, MeQueryResponse } from '@/types/auth'

/**
 * GraphQL query for fetching current user with profiles and permissions
 * Matches backend accounts/schema.py UserType
 */
const ME_QUERY = `
  query GetCurrentUser {
    me {
      id
      userId
      name
      firstName
      lastName
      phoneNumber
      email
      isActive
      isStaff
      isSuperuser
      role
      groups
      permissions
      profiles {
        profileId
        profileType
        displayName
        isPrimary
        permissions {
          resource
          action
          permissionCode
        }
        permissionCodes
        groups
        caslRules {
          action
          subject
        }
      }
      adminPermissions {
        isStaff
        isSuperuser
        djangoPermissions
      }
    }
  }
`

/**
 * Transform GraphQL response to User type
 * Converts camelCase from GraphQL to snake_case for frontend consistency
 */
function transformMeResponse(data: MeQueryResponse): User | null {
  if (!data.me) return null
  
  const me = data.me
  
  // Transform profiles from camelCase to snake_case
  const profiles: UserProfile[] = (me.profiles || []).map((profile) => ({
    profile_id: profile.profileId,
    profile_type: profile.profileType as UserProfile['profile_type'],
    display_name: profile.displayName,
    is_primary: profile.isPrimary,
    business_account_id: profile.businessAccountId ?? null,
    permissions: profile.permissions?.map((p) => ({
      resource: p.resource,
      action: p.action,
      permission_code: p.permissionCode,
    })),
    permission_codes: profile.permissionCodes || [],
    groups: profile.groups || [],
    casl_rules: profile.caslRules?.map((r) => ({
      action: r.action,
      subject: r.subject,
    })) || [],
  }))
  
  // Get permission codes from primary profile
  const primaryProfile = profiles.find((p) => p.is_primary) || profiles[0]
  const permissionCodes = primaryProfile?.permission_codes || []
  
  return {
    id: me.id,
    user_id: me.userId,
    first_name: me.firstName || '',
    last_name: me.lastName || '',
    phone_number: me.phoneNumber || '',
    email: me.email || undefined,
    name: me.name,
    role: me.role,
    is_active: me.isActive,
    is_staff: me.isStaff,
    is_superuser: me.isSuperuser,
    groups: me.groups,
    permissions: me.permissions,
    profiles,
    admin_permissions: me.adminPermissions ? {
      is_staff: me.adminPermissions.isStaff,
      is_superuser: me.adminPermissions.isSuperuser,
      django_permissions: me.adminPermissions.djangoPermissions,
    } : undefined,
    permission_codes: permissionCodes,
  }
}

/**
 * Hook to fetch current user data with profiles and permissions
 * 
 * This hook fetches user data from the GraphQL `me` query and:
 * - Transforms the response to match frontend User type
 * - Updates the auth store with the fetched user data
 * - Provides loading/error states
 * 
 * @example
 * const { user, isLoading, error, refetch } = useCurrentUser()
 * 
 * if (isLoading) return <Loading />
 * if (error) return <Error />
 * 
 * return <div>Welcome {user?.name}</div>
 */
export function useCurrentUser() {
  const { isAuthenticated, setUser, accessToken } = useAuthStore()
  
  const query = useQuery({
    queryKey: ['currentUser', accessToken ?? ''],
    queryFn: async () => {
      const response = await graphqlQuery<MeQueryResponse>(ME_QUERY)
      
      if (!response.data?.me) {
        throw new Error('Failed to fetch user data')
      }
      
      const user = transformMeResponse(response.data)
      
      // Update auth store with fetched user data
      if (user) {
        setUser(user)

        // Re-identify user across analytics on session restore (page refresh)
        setSentryUser({
          id: user.id,
          username: user.phone_number,
          role: user.role,
        })
        setUserContext(user.id, user.role)
        identifyMixpanelUser({
          id: user.id,
          email: user.email,
        })
      }
      
      return user
    },
    enabled: isAuthenticated && !!accessToken,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  })
  
  return {
    user: query.data || null,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  }
}

/**
 * Hook to get permissions from the current user
 * Extracts permissions from the primary profile
 */
export function useCurrentUserPermissions() {
  const { user, isLoading, isError } = useCurrentUser()
  
  // Get permissions from primary profile or first profile
  const activeProfile = user?.profiles?.find((p) => p.is_primary) || user?.profiles?.[0]
  const permissions = activeProfile?.permission_codes || []
  
  const hasPermission = (code: string): boolean => {
    // Superusers have all permissions
    if (user?.is_superuser || user?.admin_permissions?.is_superuser) {
      return true
    }
    return permissions.includes(code)
  }
  
  const hasAnyPermission = (codes: string[]): boolean => {
    if (user?.is_superuser || user?.admin_permissions?.is_superuser) {
      return true
    }
    return codes.some((code) => permissions.includes(code))
  }
  
  const hasAllPermissions = (codes: string[]): boolean => {
    if (user?.is_superuser || user?.admin_permissions?.is_superuser) {
      return true
    }
    return codes.every((code) => permissions.includes(code))
  }
  
  return {
    permissions,
    activeProfile,
    profiles: user?.profiles || [],
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isLoading,
    isError,
  }
}
